import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonMC from './ButtonMC';
import IconMC from './IconMC';
import openIntercom from '../utils/openIntercom';
import customHistory from '../../customHistory';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';

const STORAGE_KEY_A = 'mia_banner_no_consume_dismissed';
const STORAGE_KEY_B = 'mia_banner_insufficient_dismissed';

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}_${now.getMonth() + 1}`;
};

const isDismissed = (key) => {
  const stored = sessionStorage.getItem(key);
  return stored === getCurrentMonthKey();
};

const MiaFreeContactsExhaustedBannerMC = () => {
  const { t } = useTranslation('PaymentPlans');
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const [dismissedA, setDismissedA] = useState(false);
  const [dismissedB, setDismissedB] = useState(false);

  const slug = retailer_info?.slug;
  const miaActive = retailer_info?.mia_chatbot_active;
  const consumeBalance = retailer_info?.mia_consume_balance;
  const miaInsufficient = retailer_info?.mia_balance_insufficient;
  const postpaidBilling = retailer_info?.mia_postpaid_billing;
  const miaInfo = retailer_info?.mia_free_contacts_info;
  const freeRemaining = miaInfo?.free_contacts_remaining;

  const isScenarioA = miaActive && !consumeBalance && !postpaidBilling && miaInfo && freeRemaining === 0;
  const isScenarioB = miaActive && consumeBalance && !postpaidBilling && miaInsufficient && miaInfo && freeRemaining === 0;

  const shouldShow = (isScenarioA && !dismissedA) || (isScenarioB && !dismissedB);

  useEffect(() => {
    if (isDismissed(STORAGE_KEY_A)) setDismissedA(true);
    if (isDismissed(STORAGE_KEY_B)) setDismissedB(true);
  }, []);

  if (!shouldShow || !slug) return null;

  const activeScenario = isScenarioB && !dismissedB ? 'B' : 'A';

  const handleDismiss = () => {
    if (activeScenario === 'B') {
      sessionStorage.setItem(STORAGE_KEY_B, getCurrentMonthKey());
      setDismissedB(true);
    } else {
      sessionStorage.setItem(STORAGE_KEY_A, getCurrentMonthKey());
      setDismissedA(true);
    }
  };

  const openAdditionalChats = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'PlatformsModal',
        props: { focusAdditionalChats: true }
      }
    });
  };

  const handleActivate = () => {
    const agentsPath = `/retailers/${slug}/chatbot_ai/agents`;
    if (window.location.pathname === agentsPath) {
      openAdditionalChats();
      return;
    }

    customHistory.push(agentsPath, { openAdditionalChats: true });
  };

  const handleRecharge = () => {
    window.location.href = `/retailers/${slug}/pricing`;
  };

  const handleSupport = () => {
    openIntercom({ email: retailer_info?.email });
  };

  const description = activeScenario === 'B'
    ? t('mia_exhausted_banner_desc_insufficient')
    : t('mia_exhausted_banner_desc_no_consume');

  const buttonClass = 'tw-border-yellow-900 tw-text-yellow-900 hover:tw-border-yellow-950 hover:tw-bg-yellow-300 hover:tw-text-yellow-950 tw-px-4 tw-whitespace-nowrap';

  return (
    <div className="tw-wrapper">
      <div className="tw-box-border tw-flex tw-w-full tw-max-w-full tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-bg-yellow-300 tw-py-3 tw-pl-4 tw-pr-4 tw-text-yellow-950 lg:tw-pl-[76px]">
        <div className="tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-items-start tw-gap-2 sm:tw-w-auto sm:tw-min-w-[28rem] sm:tw-flex-1">
          <IconMC name="alert-circle-outline" size="lg" className="tw-mt-0.5 tw-shrink-0 tw-rotate-180 tw-text-yellow-950" />
          <p className="tw-mb-0 tw-min-w-0 tw-flex-1 tw-font-default tw-text-sm tw-leading-snug">
            <span className="tw-font-semibold">
              {t('mia_exhausted_banner_title')}
              {' '}
            </span>
            <span>
              {description}
            </span>
          </p>
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {activeScenario === 'A' && (
            <ButtonMC
              text={t('mia_exhausted_banner_activate')}
              variant="SECONDARY"
              outlined
              size="small"
              className={buttonClass}
              onClick={handleActivate}
            />
          )}
          <ButtonMC
            text={t('mia_exhausted_banner_recharge')}
            variant="SECONDARY"
            outlined
            size="small"
            className={buttonClass}
            onClick={handleRecharge}
          />
          <button
            type="button"
            className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-sm tw-underline tw-text-yellow-950 tw-px-2 tw-whitespace-nowrap"
            onClick={handleSupport}
          >
            {t('mia_exhausted_banner_support')}
          </button>
          <button
            type="button"
            className="tw-text-yellow-950 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-1 tw-text-lg tw-leading-none"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiaFreeContactsExhaustedBannerMC;
